import { centsToDollars, parsePrice } from "helpers/price";
import {
  IOrderBalance,
  IOrderBalanceBox,
  IOrderBalanceBoxRow,
  IOrderBalanceEvent,
  IOrderBalanceParty,
} from "types/OrderBalance";

export const formatCents = (cents: number | null | undefined): string =>
  cents === null || cents === undefined
    ? ""
    : parsePrice(centsToDollars(cents));

export const formatDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: "UTC" }) : "";

export type EventTableRow = Record<string, string | number | boolean | null>;

export const partyField = (
  partyId: string,
  field: "total" | "provider" | "fee"
): string => `party_${partyId}_${field}`;

/**
 * Flattens ledger events into DataTable rows: one row per event, three columns per provider
 * (Total / Provider / Clear Fee), exactly like the workbook's Order Overview and Payment Activity.
 */
export const buildEventRows = (
  events: IOrderBalanceEvent[],
  providers: IOrderBalanceParty[]
): EventTableRow[] =>
  events.map((event) => {
    const row: EventTableRow = {
      id: event.id,
      date: event.date,
      type: event.type,
      label: event.label,
      patient: event.patientInCents,
      clearDirect: event.clearDirectInCents,
      unallocated: event.unallocatedInCents,
      verified: event.verified,
      notes: event.notes.join(" "),
      informational: !!event.informational,
    };
    providers.forEach((provider) => {
      const amounts = event.byParty[provider.id];
      row[partyField(provider.id, "total")] = amounts?.totalInCents ?? null;
      row[partyField(provider.id, "provider")] =
        amounts?.providerInCents ?? null;
      row[partyField(provider.id, "fee")] = amounts?.clearFeeInCents ?? null;
    });
    return row;
  });

// Informational rows (the estimate) are shown for reference and never added into a total.
export const sumField = (rows: EventTableRow[], field: string): number =>
  rows.reduce(
    (sum, row) => (row.informational ? sum : sum + (Number(row[field]) || 0)),
    0
  );

/**
 * The one-line verdict. Uncollected money is Outstanding, not a balance: an order whose collected
 * money is all where it should be reads "In balance" however much the patient still owes.
 */
export const balanceVerdict = (
  ledger: IOrderBalance
): { ok: boolean; text: string } => {
  const failing = ledger.invariants.filter((i) => i.status === "FAIL").length;
  const box = ledger.balanceBox;
  if (!box.allZero || failing > 0) {
    return {
      ok: false,
      text: `Out of balance — ${failing} check${
        failing === 1 ? "" : "s"
      } failing.`,
    };
  }
  const pending = box.reportsCoverOrder
    ? ""
    : " Reported balances pending (no weekly report yet).";
  if (box.collectedInCents === 0) {
    return {
      ok: true,
      text: `Nothing collected yet${
        box.patientBalanceInCents > 0
          ? ` — ${formatCents(box.patientBalanceInCents)} to be collected`
          : ""
      }.`,
    };
  }
  if (box.patientBalanceInCents > 0) {
    return {
      ok: true,
      text: `In balance — ${formatCents(
        box.patientBalanceInCents
      )} still to be collected.${pending}`,
    };
  }
  return {
    ok: true,
    text: box.reportsCoverOrder
      ? "In balance: every balance is zero."
      : "In balance: every cash balance is zero; reported balances pending (no weekly report yet).",
  };
};

/** "Clear fee 3% · patient fee 3%": the patient fee is named whenever the order has one. */
export const feeRatesText = (ledger: IOrderBalance): string => {
  const clearFee =
    ledger.clearFeeRatePercent === null
      ? "Clear fee n/a"
      : `Clear fee ${ledger.clearFeeRatePercent}%`;
  return ledger.clearPatientFeeRatePercent
    ? `${clearFee} · patient fee ${ledger.clearPatientFeeRatePercent}%`
    : clearFee;
};

export const REPORTED_FIELDS: (keyof IOrderBalanceBoxRow)[] = [
  "reportedPaidInCents",
  "reportedTotalInCents",
  "reportedCashBalanceInCents",
  "reportedTppBalanceInCents",
];

// Clear Health has no Total / TPP columns in the workbook: those cells stay blank, never "pending".
const CLEAR_BLANK_FIELDS: (keyof IOrderBalanceBoxRow)[] = [
  "reportedTotalInCents",
  "reportedTppBalanceInCents",
];

/**
 * True when a balance-box cell is a reported figure that no weekly report covers yet — rendered as a
 * muted "pending" marker rather than $0.00, which would read as a discrepancy.
 */
export const isReportedPending = (
  balanceBox: IOrderBalanceBox,
  row: IOrderBalanceBoxRow,
  field: keyof IOrderBalanceBoxRow
): boolean =>
  !balanceBox.reportsCoverOrder &&
  REPORTED_FIELDS.includes(field) &&
  row[field] === null &&
  !(row.kind === "CLEAR_HEALTH" && CLEAR_BLANK_FIELDS.includes(field));

// The table renders "<type> — <label>"; a label that already names the type is shown on its own.
export const eventTypeText = (type: string, label: string): string => {
  if (!label || label === type) return type;
  return label.startsWith(`${type} — `) ? label : `${type} — ${label}`;
};
